import React from "react";
import { useLang } from "../LangContext";
import { useReveal } from "../hooks/useReveal";
import { Kicker } from "./Mission";
import { MapPin } from "lucide-react";
import { FOUNDER_BENJAMIN_URL, FOUNDER_CHRISTOPHE_URL } from "../assets/brand";

const BENJAMIN_IMG = FOUNDER_BENJAMIN_URL;
const CHRISTOPHE_IMG = FOUNDER_CHRISTOPHE_URL;

export const Founders = () => {
  const { t } = useLang();
  const ref = useReveal();

  const founders = [
    {
      img: BENJAMIN_IMG,
      name: t.founders.benjamin.name,
      role: t.founders.benjamin.role,
      bio: t.founders.benjamin.bio,
      accent: "from-orange-500 to-pink-500",
      testId: "founder-benjamin",
    },
    {
      img: CHRISTOPHE_IMG,
      name: t.founders.christophe.name,
      role: t.founders.christophe.role,
      bio: t.founders.christophe.bio,
      accent: "from-violet-500 to-cyan-400",
      testId: "founder-christophe",
    },
  ];

  return (
    <section
      id="founders"
      data-testid="founders-section"
      className="relative py-24 lg:py-32 reveal"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <Kicker label={t.founders.kicker} />
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            {t.founders.title.split(" ").slice(0, 1).join(" ")}{" "}
            <span className="text-baj-grad">
              {t.founders.title.split(" ").slice(1).join(" ")}
            </span>
          </h2>
          <div className="mt-5 inline-flex items-center gap-2 text-slate-400 text-sm">
            <MapPin className="w-4 h-4 text-fuchsia-300" />
            <span>Poissy, Île-de-France</span>
          </div>
          <p className="mt-6 text-lg text-slate-300 leading-relaxed">
            {t.founders.intro}
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
          {founders.map((f) => (
            <article
              key={f.testId}
              data-testid={f.testId}
              className="group relative glass rounded-[2rem] p-6 sm:p-8 hover:-translate-y-1.5 transition-transform duration-300"
            >
              <div className="flex items-start gap-5">
                <div className="relative shrink-0">
                  <div
                    className={`absolute -inset-1 rounded-full bg-gradient-to-br ${f.accent} blur-md opacity-60 group-hover:opacity-90 transition-opacity`}
                  />
                  <img
                    src={f.img}
                    alt={f.name}
                    className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-[3px] border-white/20"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-white">
                    {f.name}
                  </h3>
                  <p className="mt-1 text-sm font-semibold text-fuchsia-200/90">
                    {f.role}
                  </p>
                </div>
              </div>
              <p className="mt-6 text-slate-300 leading-relaxed">{f.bio}</p>
              <div
                className={`mt-6 h-1 w-20 rounded-full bg-gradient-to-r ${f.accent}`}
              />
            </article>
          ))}
        </div>

        <p className="mt-12 max-w-3xl text-slate-300 text-lg leading-relaxed">
          {t.founders.closing}
        </p>
      </div>
    </section>
  );
};
